import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

type InlineEditableContextValue = {
  characterCount: number;
  hasOverflow: boolean;
  isFocused: boolean;
  text: string;
  setHasOverflow: (newHasOverflow: boolean) => void;
  setIsFocused: (newIsFocused: boolean) => void;
  setText: (newText: string) => void;
};

const InlineEditableContext = createContext<InlineEditableContextValue | null>(null);

type Props = {
  children: ReactNode;
};

export const InlineEditablePropsProvider = ({ children }: Props) => {
  const [characterCount, setCharacterCount] = useState(0);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [text, setText] = useState('');

  useEffect(() => {
    setCharacterCount(text.length);
  }, [
    text,
  ]);

  const contextValue = useMemo(
    () => ({
      characterCount,
      hasOverflow,
      isFocused,
      text,
      setCharacterCount,
      setHasOverflow,
      setIsFocused,
      setText,
    }),
    [
      characterCount,
      hasOverflow,
      isFocused,
      text,
    ],
  );

  return (
    <InlineEditableContext.Provider value={contextValue}>{children}</InlineEditableContext.Provider>
  );
};

export const useInlineEditableProps = () => {
  const contextValue = useContext(InlineEditableContext);

  if (!contextValue) {
    throw new Error('useInlineEditableProps should be used inside a InlineEditablePropsProvider');
  }

  return contextValue;
};
